"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  MapPinIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const CONTENT = {
  fr: {
    h1: "Contactez-nous pour votre devis vide maison à Bruxelles",
    heroText:
      "Appelez-nous pour un besoin urgent ou décrivez votre situation dans le formulaire ci-dessous : commune, type de bien, volume approximatif. Nous revenons vers vous rapidement avec une estimation gratuite et sans engagement.",
    ctaQuote: "Demander un devis gratuit",
    mapLine: "Woluwe-Saint-Lambert — intervention dans toute la Région de Bruxelles-Capitale",

    stepsTitle: "Comment obtenir votre devis gratuit",
    steps: [
      {
        n: "1",
        title: "Décrivez votre besoin",
        text: "Par téléphone ou via le formulaire : commune du bien, type de bien, urgence, et si possible quelques photos pour situer le volume.",
      },
      {
        n: "2",
        title: "Estimation gratuite",
        text: "Sur base de ces éléments, nous évaluons le volume à évacuer, l'accès au bien et les objets susceptibles d'être rachetés.",
      },
      {
        n: "3",
        title: "Devis sans engagement",
        text: "Vous recevez une proposition claire et décidez librement de la suite : acceptation, report, ou simple prise d'information.",
      },
    ],

    situationsTitle: "Dans quelles situations nous contacter",
    situationsP1Before1: "La plupart des demandes concernent un ",
    situationsP1Link1: "débarras de maison complète",
    situationsP1Mid1: " après un déménagement ou une succession, un ",
    situationsP1Link2: "débarras d'appartement",
    situationsP1Mid2:
      " entre deux locations, ou un espace isolé — cave, grenier, garage — à vider avant des travaux. D'autres demandes portent uniquement sur un ",
    situationsP1Link3: "rachat d'objets de valeur",
    situationsP1After: " repéré dans un bien.",
    situationsP2Before:
      "Dans tous les cas, un premier contact suffit pour lancer l'estimation : nous adaptons ensuite le devis au périmètre réel de votre demande, que ce soit un bien entier ou un seul espace. Les 19 communes de la ",
    situationsP2Link: "Région de Bruxelles-Capitale",
    situationsP2After: " sont couvertes.",

    knowTitle: "Ce qu'il faut savoir avant de nous contacter",
    knowP1Before:
      "Le prix d'un vide maison dépend du volume réel à évacuer, de la nature des objets et des conditions d'accès : il ne peut donc pas être fixé sans échange préalable. Notre page ",
    knowP1Link: "tarifs et facteurs de prix",
    knowP1After: " détaille les éléments qui influencent une estimation.",
    knowP2:
      "Les objets encore en bon état sont examinés avant toute évacuation : ce qui peut être racheté est déduit du devis, le reste est orienté vers les filières adaptées (recyclage, don, déchet selon la nature). Pour préparer votre demande, il suffit d'identifier ce que vous souhaitez conserver et de signaler tout élément particulier — étage, ascenseur, accès restreint — au moment du premier contact.",

    whyTitle: "Pourquoi passer par ce formulaire de contact",
    whyItems: [
      "Estimation gratuite avant toute intervention",
      "Aucun engagement tant que vous n'avez pas validé le devis",
      "Réponse à votre demande sous 24h ouvrées",
      "Rachat des objets de valeur déduit du montant final",
      "Intervention dans les 19 communes de Bruxelles",
      "Demande possible par téléphone, formulaire ou photos",
    ],
    whySeeRealisations: "Voir des exemples d'interventions réalisées",
    whySeeAvis: "Consulter les avis clients",

    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Comment obtenir un devis pour mon vide maison ?",
        a: "Appelez-nous directement ou remplissez le formulaire ci-dessous en précisant la commune, le type de bien et l'urgence de votre demande. Vous pouvez aussi joindre des photos pour nous aider à évaluer le volume avant tout échange.",
      },
      {
        q: "Le devis est-il vraiment gratuit et sans engagement ?",
        a: "Oui. L'estimation ne vous engage à rien : vous restez libre d'accepter ou non la proposition. Aucun frais n'est facturé pour l'établir, que ce soit par téléphone, par formulaire ou lors d'un passage sur place.",
      },
      {
        q: "Sous quel délai me répondez-vous ?",
        a: "Un premier retour est donné sous 24h ouvrées après votre demande, par téléphone ou via les coordonnées transmises dans le formulaire. La date d'intervention elle-même dépend ensuite de votre disponibilité et de l'urgence du dossier.",
      },
      {
        q: "Dois-je être présent lors de l'estimation du devis ?",
        a: "Ce n'est pas obligatoire. De nombreux devis sont établis sur base de photos et d'une description précise du bien (surface, accès, volume approximatif). Une visite reste possible si la situation le demande, notamment pour les gros volumes.",
      },
      {
        q: "Que faire si je ne connais pas encore le volume exact à évacuer ?",
        a: "C'est le cas de la majorité des demandes. Décrivez la situation du mieux possible — type de bien, pièces concernées, objets visibles — et nous affinons l'estimation avec vous, par téléphone ou lors d'un passage si nécessaire.",
      },
      {
        q: "Puis-je vous contacter pour un seul espace, comme une cave ou un grenier ?",
        a: "Oui, une demande peut porter sur un espace isolé (cave, grenier, garage) comme sur un bien complet. Précisez-le dans le formulaire ou par téléphone : le devis est établi en fonction du périmètre réel de l'intervention.",
      },
      {
        q: "Intervenez-vous dans toutes les communes de Bruxelles ?",
        a: "Oui, dans les 19 communes de la Région de Bruxelles-Capitale. Indiquez simplement la commune du bien dans votre demande : les contraintes locales (accès, stationnement, syndic) sont prises en compte pour organiser l'intervention.",
      },
    ],
  },
  nl: {
    h1: "Neem contact met ons op voor uw offerte huis leegmaken in Brussel",
    heroText:
      "Bel ons voor een dringende behoefte of beschrijf uw situatie in het onderstaande formulier: gemeente, type pand, geschat volume. Wij nemen snel contact met u op met een gratis en vrijblijvende schatting.",
    ctaQuote: "Vraag een gratis offerte aan",
    mapLine: "Woluwe-Saint-Lambert — interventie in het volledige Brussels Hoofdstedelijk Gewest",

    stepsTitle: "Hoe u uw gratis offerte verkrijgt",
    steps: [
      {
        n: "1",
        title: "Beschrijf uw behoefte",
        text: "Per telefoon of via het formulier: gemeente van het pand, type pand, urgentie, en indien mogelijk enkele foto's om het volume in te schatten.",
      },
      {
        n: "2",
        title: "Gratis schatting",
        text: "Op basis van deze gegevens evalueren wij het af te voeren volume, de toegang tot het pand en de voorwerpen die mogelijk kunnen worden aangekocht.",
      },
      {
        n: "3",
        title: "Vrijblijvende offerte",
        text: "U ontvangt een duidelijk voorstel en beslist vrij over het vervolg: aanvaarding, uitstel, of gewoon een verkennend gesprek.",
      },
    ],

    situationsTitle: "In welke situaties kunt u ons contacteren",
    situationsP1Before1: "De meeste aanvragen betreffen een ",
    situationsP1Link1: "volledige ontruiming van een huis",
    situationsP1Mid1: " na een verhuizing of nalatenschap, een ",
    situationsP1Link2: "ontruiming van een appartement",
    situationsP1Mid2:
      " tussen twee verhuurperiodes, of een geïsoleerde ruimte — kelder, zolder, garage — die vóór werken moet worden leeggemaakt. Andere aanvragen betreffen uitsluitend een ",
    situationsP1Link3: "inkoop van waardevolle voorwerpen",
    situationsP1After: " die in een pand worden aangetroffen.",
    situationsP2Before:
      "In elk geval volstaat een eerste contact om de schatting te starten: wij passen de offerte vervolgens aan de werkelijke omvang van uw aanvraag aan, of het nu om een volledig pand of één enkele ruimte gaat. De 19 gemeenten van het ",
    situationsP2Link: "Brussels Hoofdstedelijk Gewest",
    situationsP2After: " worden gedekt.",

    knowTitle: "Wat u moet weten voordat u ons contacteert",
    knowP1Before:
      "De prijs van een huis leegmaken hangt af van het werkelijke volume dat moet worden afgevoerd, de aard van de voorwerpen en de toegangsomstandigheden: ze kan dus niet worden vastgelegd zonder voorafgaand overleg. Onze pagina ",
    knowP1Link: "tarieven en prijsfactoren",
    knowP1After: " geeft een overzicht van de elementen die een schatting beïnvloeden.",
    knowP2:
      "Voorwerpen die nog in goede staat zijn, worden onderzocht vóór elke afvoer: wat kan worden aangekocht, wordt afgetrokken van de offerte, de rest wordt naar de geschikte kanalen geleid (recyclage, schenking, afval naargelang de aard). Om uw aanvraag voor te bereiden, volstaat het te bepalen wat u wenst te behouden en elk bijzonder element — verdieping, lift, beperkte toegang — te melden bij het eerste contact.",

    whyTitle: "Waarom via dit contactformulier gaan",
    whyItems: [
      "Gratis schatting vóór elke interventie",
      "Geen enkel engagement zolang u de offerte niet heeft goedgekeurd",
      "Antwoord op uw aanvraag binnen 24 werkuren",
      "Inkoop van waardevolle voorwerpen afgetrokken van het eindbedrag",
      "Interventie in de 19 gemeenten van Brussel",
      "Aanvraag mogelijk per telefoon, formulier of foto's",
    ],
    whySeeRealisations: "Bekijk voorbeelden van uitgevoerde interventies",
    whySeeAvis: "Bekijk de klantenbeoordelingen",

    faqTitle: "Veelgestelde vragen",
    faq: [
      {
        q: "Hoe kan ik een offerte krijgen voor het leegmaken van mijn huis?",
        a: "Bel ons rechtstreeks of vul het onderstaande formulier in met vermelding van de gemeente, het type pand en de urgentie van uw aanvraag. U kunt ook foto's toevoegen om ons te helpen het volume in te schatten vóór elk contact.",
      },
      {
        q: "Is de offerte echt gratis en vrijblijvend?",
        a: "Ja. De schatting verplicht u tot niets: u blijft vrij om het voorstel al dan niet te aanvaarden. Er worden geen kosten aangerekend om ze op te stellen, of het nu telefonisch, via het formulier of tijdens een bezoek ter plaatse is.",
      },
      {
        q: "Binnen welke termijn krijg ik een antwoord van u?",
        a: "Een eerste antwoord wordt gegeven binnen 24 werkuren na uw aanvraag, telefonisch of via de contactgegevens die u in het formulier heeft doorgegeven. De datum van de interventie zelf hangt vervolgens af van uw beschikbaarheid en de urgentie van het dossier.",
      },
      {
        q: "Moet ik aanwezig zijn bij het opstellen van de offerte?",
        a: "Dat is niet verplicht. Veel offertes worden opgesteld op basis van foto's en een nauwkeurige beschrijving van het pand (oppervlakte, toegang, geschat volume). Een bezoek ter plaatse blijft mogelijk indien de situatie dat vereist, met name bij grote volumes.",
      },
      {
        q: "Wat als ik het exacte af te voeren volume nog niet ken?",
        a: "Dat is het geval bij de meerderheid van de aanvragen. Beschrijf de situatie zo goed mogelijk — type pand, betrokken kamers, zichtbare voorwerpen — en wij verfijnen de schatting samen met u, telefonisch of tijdens een bezoek indien nodig.",
      },
      {
        q: "Kan ik u contacteren voor slechts één ruimte, zoals een kelder of zolder?",
        a: "Ja, een aanvraag kan zowel een geïsoleerde ruimte (kelder, zolder, garage) als een volledig pand betreffen. Vermeld dit in het formulier of telefonisch: de offerte wordt opgesteld op basis van de werkelijke omvang van de interventie.",
      },
      {
        q: "Bent u actief in alle gemeenten van Brussel?",
        a: "Ja, in de 19 gemeenten van het Brussels Hoofdstedelijk Gewest. Vermeld eenvoudigweg de gemeente van het pand in uw aanvraag: lokale beperkingen (toegang, parkeren, syndicus) worden in rekening gebracht bij de organisatie van de interventie.",
      },
    ],
  },
  en: {
    h1: "Contact us for your house clearance quote in Brussels",
    heroText:
      "Call us for an urgent need, or describe your situation in the form below: commune, type of property, approximate volume. We'll get back to you quickly with a free, no-obligation estimate.",
    ctaQuote: "Request a free quote",
    mapLine: "Woluwe-Saint-Lambert — service across the whole Brussels-Capital Region",

    stepsTitle: "How to get your free quote",
    steps: [
      {
        n: "1",
        title: "Describe your need",
        text: "By phone or via the form: commune of the property, type of property, urgency, and if possible a few photos to help gauge the volume.",
      },
      {
        n: "2",
        title: "Free estimate",
        text: "Based on this information, we assess the volume to be removed, access to the property, and any items that could be bought back.",
      },
      {
        n: "3",
        title: "No-obligation quote",
        text: "You receive a clear proposal and decide freely what happens next: acceptance, postponement, or simply gathering information.",
      },
    ],

    situationsTitle: "When to contact us",
    situationsP1Before1: "Most requests involve a ",
    situationsP1Link1: "full house clearance",
    situationsP1Mid1: " after a move or an estate settlement, an ",
    situationsP1Link2: "apartment clearance",
    situationsP1Mid2:
      " between two tenancies, or a single space — cellar, attic, garage — to be cleared before renovation work. Other requests concern only the ",
    situationsP1Link3: "buyback of valuable items",
    situationsP1After: " found in a property.",
    situationsP2Before:
      "In all cases, an initial contact is enough to start the estimate: we then tailor the quote to the actual scope of your request, whether it's a whole property or a single space. All 19 communes of the ",
    situationsP2Link: "Brussels-Capital Region",
    situationsP2After: " are covered.",

    knowTitle: "What you should know before contacting us",
    knowP1Before:
      "The price of a house clearance depends on the actual volume to be removed, the nature of the items, and the access conditions: it therefore cannot be set without a prior discussion. Our ",
    knowP1Link: "prices and pricing factors",
    knowP1After: " page details the elements that influence an estimate.",
    knowP2:
      "Items still in good condition are examined before any removal: whatever can be bought back is deducted from the quote, and the rest is directed to the appropriate channels (recycling, donation, or waste, depending on its nature). To prepare your request, simply identify what you'd like to keep and mention any specific details — floor level, lift, restricted access — at the time of first contact.",

    whyTitle: "Why use this contact form",
    whyItems: [
      "Free estimate before any job",
      "No obligation until you've approved the quote",
      "Response to your request within 24 business hours",
      "Buyback of valuable items deducted from the final amount",
      "Service across the 19 communes of Brussels",
      "Request possible by phone, form or photos",
    ],
    whySeeRealisations: "See examples of completed jobs",
    whySeeAvis: "See customer reviews",

    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "How do I get a quote for my house clearance?",
        a: "Call us directly or fill in the form below, specifying the commune, the type of property and the urgency of your request. You can also attach photos to help us assess the volume before any further discussion.",
      },
      {
        q: "Is the quote really free and with no obligation?",
        a: "Yes. The estimate doesn't commit you to anything: you remain free to accept the proposal or not. No fee is charged to prepare it, whether by phone, via the form, or during an on-site visit.",
      },
      {
        q: "How quickly do you respond?",
        a: "An initial response is given within 24 business hours of your request, by phone or via the contact details provided in the form. The actual date of the job then depends on your availability and the urgency of the case.",
      },
      {
        q: "Do I need to be present for the quote estimation?",
        a: "It's not required. Many quotes are prepared based on photos and an accurate description of the property (surface area, access, approximate volume). An on-site visit remains possible if the situation calls for it, particularly for large volumes.",
      },
      {
        q: "What if I don't yet know the exact volume to be removed?",
        a: "That's the case for most requests. Describe the situation as best you can — type of property, rooms involved, visible items — and we'll refine the estimate together with you, by phone or during a visit if needed.",
      },
      {
        q: "Can I contact you for just one space, like a cellar or attic?",
        a: "Yes, a request can concern a single space (cellar, attic, garage) just as much as an entire property. Mention it in the form or by phone: the quote is drawn up based on the actual scope of the job.",
      },
      {
        q: "Do you operate in all communes of Brussels?",
        a: "Yes, in all 19 communes of the Brussels-Capital Region. Simply indicate the commune of the property in your request: local constraints (access, parking, building manager) are taken into account when organising the job.",
      },
    ],
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 480px at 15% -10%, rgba(27,75,51,0.06), transparent 55%)" }}
        />
        <div className="relative mx-auto max-w-[1000px] px-6 py-20 lg:px-10 lg:py-24">
          <h1 className="font-serif text-4xl font-black md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaQuote}
            </CtaLink>
          </div>

          <div className="mt-8 flex flex-wrap gap-6 text-sm">
            <span className="flex items-center gap-2 text-ink/60">
              <MapPinIcon className="h-5 w-5" />
              {c.mapLine}
            </span>
          </div>
        </div>
      </section>

      {/* COMMENT OBTENIR UN DEVIS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.stepsTitle}
          </h2>
          <ol className="mt-8 space-y-6">
            {c.steps.map((step) => (
              <li key={step.n} className="flex gap-5 rounded-3xl border border-sand-line bg-sand p-6">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                  {step.n}
                </span>
                <div>
                  <p className="font-bold text-ink">{step.title}</p>
                  <p className="mt-1 text-sm text-neutral-600">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* QUAND NOUS CONTACTER */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.situationsTitle}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.situationsP1Before1}
            <Link href="/debarras-maison" className="font-semibold text-accent underline underline-offset-4">
              {c.situationsP1Link1}
            </Link>
            {c.situationsP1Mid1}
            <Link href="/debarras-appartement" className="font-semibold text-accent underline underline-offset-4">
              {c.situationsP1Link2}
            </Link>
            {c.situationsP1Mid2}
            <Link href="/rachat-objets" className="font-semibold text-accent underline underline-offset-4">
              {c.situationsP1Link3}
            </Link>
            {c.situationsP1After}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.situationsP2Before}
            <Link href="/vide-maison" className="font-semibold text-accent underline underline-offset-4">
              {c.situationsP2Link}
            </Link>
            {c.situationsP2After}
          </p>
        </div>
      </section>

      {/* OBJECTIONS / CE QU'IL FAUT SAVOIR */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <ClockIcon className="h-8 w-8 text-accent" />
            <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.knowTitle}
            </h2>
          </div>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.knowP1Before}
            <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
              {c.knowP1Link}
            </Link>
            {c.knowP1After}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.knowP2}
          </p>
        </div>
      </section>

      {/* POURQUOI NOUS CONTACTER */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.whyTitle}
          </h2>
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.whyItems.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 rounded-2xl border border-sand-line bg-white p-4 text-sm font-semibold text-ink"
              >
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            <Link
              href="/realisations"
              className="inline-flex items-center gap-2 font-bold text-accent hover:text-accent-dark"
            >
              {c.whySeeRealisations}
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
            <Link
              href="/avis"
              className="inline-flex items-center gap-2 font-bold text-accent hover:text-accent-dark"
            >
              {c.whySeeAvis}
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">{c.faqTitle}</h2>
          <div className="mt-8 space-y-4">
            {c.faq.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
